import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { SourceCombobox, type SourceComboboxOption } from "@/components/excerpts/SourceCombobox";
import { LibraryCategoryBadge } from "@/components/library/LibraryCategoryBadge";
import { deriveLibraryTitle, getLibrarySourceTypeLabel, type LibraryItem } from "@/lib/library";
import type { LibraryCategory } from "@/lib/libraryCategories";
import type { DetectedSourceIdentifier } from "@/lib/sourceIdentifiers";

type Props = {
  selectedLibraryItem: LibraryItem | null;
  returnTo: string;
  libraryItemId: string;
  setLibraryItemId: (id: string) => void;
  comboboxOptions: SourceComboboxOption[];
  citationPreview: string | null;
  citationIncomplete: boolean;
  inheritedCategories: LibraryCategory[];
  handleOpenAddSource: (q: string, identifier?: DetectedSourceIdentifier | null) => void;
  handleOpenEditSource: () => void;
  openUnlinkDialog: () => void;
};

export function LinkedSourceBanner({
  selectedLibraryItem, returnTo,
}: { selectedLibraryItem: LibraryItem; returnTo: string }) {
  const isPodcast = selectedLibraryItem.source_type === "podcast" && !!selectedLibraryItem.container_title;
  const episodeTitle = deriveLibraryTitle(selectedLibraryItem);
  return (
    <Card className="shadow-soft">
      <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            Linked source · {getLibrarySourceTypeLabel(selectedLibraryItem.source_type)}
          </p>
          {isPodcast ? (
            <p className="text-sm text-muted-foreground">
              <span className="italic">{selectedLibraryItem.container_title}</span>
              {" · "}
              <span>&ldquo;{episodeTitle}&rdquo;</span>
              {selectedLibraryItem.author_text ? ` — ${selectedLibraryItem.author_text}` : ""}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {episodeTitle}
              {selectedLibraryItem.author_text ? ` — ${selectedLibraryItem.author_text}` : ""}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/excerpts?libraryItem=${selectedLibraryItem.id}&returnTo=${encodeURIComponent(returnTo || `my-library:${selectedLibraryItem.id}`)}`}>
            <Button variant="outline">View related excerpts</Button>
          </Link>
          <Link to={`/my-library?item=${selectedLibraryItem.id}&returnTo=${encodeURIComponent(returnTo || `my-library:${selectedLibraryItem.id}`)}`}>
            <Button variant="outline"><ArrowUpRight className="h-4 w-4" /> Open in My Library</Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

export function SourcePickerSection({
  libraryItemId, setLibraryItemId, comboboxOptions,
  citationPreview, citationIncomplete,
  handleOpenAddSource, handleOpenEditSource, openUnlinkDialog,
}: Props) {
  return (
    <div className="space-y-2">
      <Label>Source (from My Library)</Label>
      <SourceCombobox
        value={libraryItemId}
        options={comboboxOptions}
        onChange={(nextId) => setLibraryItemId(nextId)}
        onCreateNew={handleOpenAddSource}
      />
      {citationPreview ? (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs">
          <span className="font-medium text-foreground">Citation preview · </span>
          <span className="text-muted-foreground">{citationPreview}</span>
          {citationIncomplete && (
            <span className="ml-2 text-amber-600 dark:text-amber-400">
              Source missing author or year — citation will be incomplete.{" "}
              <button
                type="button"
                onClick={handleOpenEditSource}
                className="underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-300"
              >
                Edit source
              </button>
            </span>
          )}
          {libraryItemId && (
            <div className="mt-1.5 flex gap-3">
              <button
                type="button"
                onClick={handleOpenEditSource}
                className="text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                Edit source
              </button>
              <button
                type="button"
                onClick={openUnlinkDialog}
                className="text-muted-foreground underline underline-offset-2 hover:text-destructive"
              >
                Unlink source
              </button>
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Author and publication metadata come from the linked library item — keep your library record clean and citations stay clean. Need to fix attribution? Edit the source in My Library.
        </p>
      )}
      <p className="text-[11px] text-muted-foreground">
        Tip: paste a DOI, ISBN, or URL into the search to add a new source automatically.
      </p>
    </div>
  );
}

export function InheritedCategoriesSection({
  libraryItemId, inheritedCategories,
}: { libraryItemId: string; inheritedCategories: LibraryCategory[] }) {
  return (
    <div className="space-y-2">
      <Label>Categories (from source)</Label>
      {libraryItemId ? (
        inheritedCategories.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {inheritedCategories.map((cat) => (
              <LibraryCategoryBadge key={cat.id} category={cat} />
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No categories on this source yet. Manage categories on the library item in{" "}
            <Link to={`/my-library?item=${libraryItemId}`} className="underline underline-offset-2 hover:text-foreground">
              My Library
            </Link>
            .
          </p>
        )
      ) : (
        <p className="text-xs text-muted-foreground">
          Link a source to inherit its categories.
        </p>
      )}
    </div>
  );
}
